import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaHome, 
  FaSignInAlt, 
  FaBook, 
  FaEnvelope,
  FaUsers, 
  FaChalkboardTeacher, 
  FaGraduationCap,
  FaUserCheck,
  FaClipboardCheck,
  FaChartLine,
  FaUserGraduate,
  FaNewspaper
} from 'react-icons/fa';

const BottomNavigation = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const getNavItems = () => {
    const pathname = location.pathname;
    
    // Check if user is on admin pages
    if (pathname.startsWith('/admin')) {
      return [
        { href: '/admin', icon: FaHome, label: t('admin.dashboard'), id: 'dashboard' },
        { href: '/admin/students', icon: FaGraduationCap, label: t('admin.students'), id: 'students' },
        { href: '/admin/teachers', icon: FaChalkboardTeacher, label: t('admin.teachers'), id: 'teachers' },
        { href: '/admin/courses', icon: FaBook, label: t('admin.courses'), id: 'courses' },
        { href: '/admin/accounts', icon: FaUserCheck, label: t('admin.userAccounts'), id: 'user-accounts' },
      ];
    }
    
    // Check if user is on teacher pages
    if (pathname.startsWith('/teacher')) {
      return [
        { href: '/teacher', icon: FaHome, label: t('teacher.dashboard'), id: 'dashboard' },
        { href: '/teacher/attendance', icon: FaClipboardCheck, label: t('teacher.attendance'), id: 'attendance' },
        { href: '/teacher/scores', icon: FaChartLine, label: t('teacher.manageScores'), id: 'scores' },
        { href: '/teacher/courses', icon: FaBook, label: t('teacher.myCourses'), id: 'courses' },
      ];
    }
    
    // Check if user is on student pages
    if (pathname.startsWith('/student')) {
      return [
        { href: '/student', icon: FaHome, label: t('student.dashboard'), id: 'dashboard' },
        { href: '/student/attendance', icon: FaClipboardCheck, label: t('student.attendance'), id: 'attendance' },
        { href: '/student/scores', icon: FaChartLine, label: t('student.myGrades'), id: 'scores' },
        { href: '/student/courses', icon: FaBook, label: t('student.myCourses'), id: 'courses' },
      ];
    }
    
    // Default/main navigation for regular visitors
    return [
      { href: '/', icon: FaHome, label: t('navbar.home'), id: 'home' },
      { href: '/login', icon: FaSignInAlt, label: t('navbar.login'), id: 'login' },
      { href: '/news', icon: FaNewspaper, label: t('navbar.news'), id: 'news' },
      { href: '/courses', icon: FaBook, label: t('navbar.courses'), id: 'courses' },
      { href: '/contact', icon: FaEnvelope, label: t('navbar.contact'), id: 'contact' },
    ];
  };

  const navItems = getNavItems();
  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    if (href === '/admin' || href === '/teacher' || href === '/student') {
      return location.pathname === href || location.pathname === href + '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="bg-white/80 backdrop-blur-xl border-t border-white/20 shadow-lg">
        <div className="pb-safe">
          <div className="flex justify-around items-center py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <motion.div
                  key={item.id}
                  className="flex-1"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Link
                    to={item.href}
                    className={`flex flex-col items-center justify-center py-2 px-1 relative ${
                      active ? 'text-safir-primary' : 'text-gray-600 hover:text-safir-primary'
                    } transition-colors duration-200`}
                  >
                    <motion.div
                      className={`p-2 rounded-full transition-all duration-300 ${
                        active 
                          ? 'bg-safir-primary/10 shadow-md' 
                          : 'hover:bg-gray-100'
                      }`}
                      whileHover={{ y: -2 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <Icon 
                        size={18} 
                        className={`${active ? 'text-safir-primary' : 'text-gray-600'}`}
                      />
                    </motion.div>
                    
                    <span className="text-xs mt-1 font-medium truncate max-w-full px-1">
                      {item.label}
                    </span>
                    
                    {active && (
                      <motion.div
                        className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-safir-primary rounded-full"
                        layoutId="bottomNavActive"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 20,
                        }}
                      />
                    )}
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BottomNavigation;
